import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Zap,
  Clock,
  Infinity as InfinityIcon,
  Heart,
  ShoppingBag,
  ArrowRight,
  Check,
  Sparkles,
  Dumbbell,
  Target,
  Users,
  Shield,
  Quote,
  Award,
  PlayCircle,
  Calendar,
  TrendingUp,
} from "lucide-react";
import { toast } from "sonner";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { api } from "@/lib/api";
import { PROGRAMS_PERKS } from "@/lib/content";
import SEO, { faqJsonLd } from "@/components/SEO";

const PERK_ICONS = { Zap, Clock, Infinity: InfinityIcon, Heart };

/* ---------- Static enrichment per program (UI only) ---------- */
const PROGRAM_META = {
  "choose-you": {
    duration: "6 weeks",
    level: "Beginner → Intermediate",
    focus: "Full-body strength",
    goals: "Confidence · Structure · Progressive overload",
    accent: "from-[#F3C9D3] to-[#E8A6B8]",
    badge: "Most Popular",
    whoFor: [
      "Women who want a guided, structured plan",
      "Beginners ready to step into the weights area",
      "Anyone who's tried programs that didn't 'fit' real life",
    ],
  },
  "strong-foundations": {
    duration: "Self-paced",
    level: "Complete Beginner",
    focus: "Movement fundamentals",
    goals: "Squat · Hinge · Push · Pull",
    accent: "from-[#F6EFE9] to-[#F3C9D3]",
    badge: "Best For Beginners",
    whoFor: [
      "Women brand new to strength training",
      "Anyone who wants to lift properly from day one",
      "Women rebuilding after time away from the gym",
    ],
  },
  "glutes-legs-core": {
    duration: "8 weeks",
    level: "Beginner → Intermediate",
    focus: "Lower body & core",
    goals: "Shape · Strength · Stability",
    accent: "from-[#F3C9D3] to-[#F6EFE9]",
    badge: "Strength + Shape",
    whoFor: [
      "Women wanting focused lower-body progress",
      "Anyone who loves a glute & core focus",
      "Women looking for a structured progression block",
    ],
  },
};

const PROGRAM_BENEFITS = [
  {
    title: "Designed for women's bodies",
    body: "Programmed by a women's strength coach — not just adapted from a men's template. Built around real hormones, real life, real recovery.",
    icon: Heart,
  },
  {
    title: "Beginner-safe progressions",
    body: "Every program starts where you are. Movement fundamentals first, intensity later — no ego-lifting, no guesswork.",
    icon: Shield,
  },
  {
    title: "Train at your own pace",
    body: "Self-paced and lifetime access. Pause when life gets busy, return when you're ready — your plan is always there.",
    icon: Clock,
  },
  {
    title: "Built for real-life gyms",
    body: "Use a commercial gym, a home setup or just dumbbells — every program includes simple, sensible swaps.",
    icon: Dumbbell,
  },
];

const COMPARISON_ROWS = [
  { label: "Best for", values: ["Confidence + structure", "Total beginners", "Lower-body focus"] },
  { label: "Duration", values: ["6 weeks", "Self-paced", "8 weeks"] },
  { label: "Level", values: ["Beginner → Inter.", "Complete beginner", "Beginner → Inter."] },
  { label: "Focus", values: ["Full body strength", "Movement basics", "Glutes, legs, core"] },
  { label: "Sessions / week", values: ["3–4", "2–3", "3–4"] },
];

const TRANSFORMATION_TESTIMONIALS = [
  {
    quote: "I finally know what I'm doing in the gym.",
    body:
      "Strong Foundations taught me how to actually lift. I went from guessing my way around machines to confidently programming my own week.",
    name: "Ella",
    role: "Strong Foundations",
  },
  {
    quote: "My strongest deadlift, ever.",
    body:
      "Six weeks of Choose You and I PB'd my deadlift by 20kg — but the real win was walking into the gym without that anxious knot in my stomach.",
    name: "Sarah",
    role: "Choose You",
  },
  {
    quote: "My glutes have never felt this strong.",
    body:
      "The 8-week structure of Glutes, Legs & Core was a game-changer. I feel stronger walking up stairs, lifting my kids, just… moving through life.",
    name: "Megan",
    role: "Glutes, Legs & Core",
  },
];

const FAQ_ITEMS = [
  {
    q: "Are these programs beginner friendly?",
    a: "Yes — every program is built to be beginner-safe. Strong Foundations is specifically for complete beginners. Choose You and Glutes, Legs & Core are beginner to intermediate, with simple regressions if you need them.",
  },
  {
    q: "Do I need a gym to use these programs?",
    a: "A gym is ideal, but you can adapt most workouts to a home setup with dumbbells and bands. Every program includes sensible exercise swaps.",
  },
  {
    q: "How long do I have access?",
    a: "Lifetime access. Once you've purchased, the program is yours to keep, repeat and revisit whenever you want.",
  },
  {
    q: "What's the difference between a program and 1:1 coaching?",
    a: "Programs are self-paced templates you follow on your own. 1:1 online coaching for women includes a personalised plan, weekly check-ins, technique reviews and direct support from Maya.",
  },
  {
    q: "Can I do these postpartum?",
    a: "For specific postpartum strength coaching, I recommend 1:1 online coaching where I can tailor your plan to your stage of recovery. The programs are excellent once you're cleared and ready for structured strength training.",
  },
  {
    q: "Do I get receipts and instant access?",
    a: "Yes — checkout is secure via Stripe, and your program is delivered to your email immediately after payment.",
  },
];

/* ---------- Helpers ---------- */
function formatPrice(p) {
  const symbol = { GBP: "£", USD: "$", EUR: "€" }[p.currency] || "£";
  return `${symbol}${Number(p.price).toFixed(0)}`;
}

/* ---------- Page ---------- */
export default function Programs() {
  const [programs, setPrograms] = useState([]);
  const [buying, setBuying] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/programs")
      .then((r) => setPrograms(r.data))
      .catch(() => toast.error("Could not load programs"))
      .finally(() => setLoading(false));
  }, []);

  const buy = async (programId) => {
    setBuying(programId);
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/checkout/session", {
        program_id: programId,
        origin_url: origin,
      });
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error("Could not start checkout");
        setBuying(null);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout");
      setBuying(null);
    }
  };

  return (
    <div data-testid="programs-page" className="bg-[#F6EFE9]">
      <SEO
        title="Women's Fitness Programs | Strength Training for Women — HerFitHub"
        description="Premium self-paced women's fitness programs designed by London-based strength coach Maya. Beginner-friendly strength training for women — Choose You, Strong Foundations and Glutes, Legs & Core."
        path="/programs"
        keywords="women's fitness programs, strength training for women, women's strength training, beginner strength program for women, glutes legs core program, women's strength coach London, online strength program for women"
        jsonLd={[faqJsonLd(FAQ_ITEMS)]}
      />
      <Hero />
      <PerksStrip />
      <ProgramsGrid programs={programs} buy={buy} buying={buying} loading={loading} />
      <ProgramBenefits />
      <Comparison programs={programs} />
      <Transformations />
      <FAQ />
      <FinalCTA />
    </div>
  );
}

/* ---------- HERO ---------- */
function Hero() {
  return (
    <section className="relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 lg:pt-28 pb-16">
        <div className="max-w-4xl mx-auto text-center fade-in">
          <p className="eyebrow mb-5" data-testid="programs-eyebrow">
            Programs · Self-Paced Strength Training For Women
          </p>
          <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[1.04] text-[#3A3A3A]">
            Strength built{" "}
            <span className="italic text-[#E8A6B8]">your way</span>
          </h1>
          <p className="heart-divider mt-5">♡</p>
          <p className="mt-6 text-base sm:text-lg leading-relaxed text-[#5C5C5C] max-w-2xl mx-auto">
            Three women-only strength programs designed to help you build
            confidence, structure and capability — whether you're picking up
            your first dumbbell or returning to the weights room after time away.
          </p>

          {/* Pre-launch notice */}
          <div className="mt-8 flex justify-center fade-in" style={{ animationDelay: "150ms" }}>
            <div
              className="inline-flex items-start gap-3 bg-white/85 backdrop-blur-sm border border-[#F3C9D3]/70 rounded-2xl px-5 py-4 max-w-xl shadow-[0_8px_22px_rgba(232,166,184,0.16)] text-left"
              data-testid="programs-prelaunch-notice"
              role="status"
            >
              <Sparkles size={16} className="text-[#E8A6B8] flex-shrink-0 mt-0.5" strokeWidth={1.6} />
              <div>
                <p className="text-[10.5px] sm:text-[11px] uppercase tracking-[0.22em] font-semibold text-[#3A3A3A]">
                  Currently Being Refined For Launch
                </p>
                <p className="mt-1 text-xs sm:text-sm text-[#5C5C5C] leading-snug">
                  Full programs are coming soon. Join the waitlist below to be the first to know — and to enjoy founding-member pricing.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <a href="#programs" className="btn-pink" data-testid="hero-explore-programs">
              Join The Waitlist <ArrowRight size={16} />
            </a>
            <Link to="/booking" className="btn-outline" data-testid="hero-bespoke-link">
              Want Bespoke Coaching?
            </Link>
          </div>
          <p className="mt-8 text-[11px] sm:text-xs uppercase tracking-[0.16em] text-[#5C5C5C] flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5">
            <span>Beginner friendly</span>
            <span className="text-[#E8A6B8]">•</span>
            <span>Lifetime access</span>
            <span className="text-[#E8A6B8]">•</span>
            <span>Train at your own pace</span>
          </p>
        </div>
      </div>
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-[#F3C9D3]/40 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-32 -left-32 w-[420px] h-[420px] rounded-full bg-[#E8A6B8]/15 blur-3xl"
      />
    </section>
  );
}

/* ---------- PERKS STRIP ---------- */
function PerksStrip() {
  return (
    <section aria-label="Program perks" className="bg-white border-y border-[#F3C9D3]/40">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-6 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
        {PROGRAMS_PERKS.map((p) => {
          const I = PERK_ICONS[p.icon] || Heart;
          return (
            <div
              key={p.label}
              className="flex items-center justify-center gap-2 text-[11px] sm:text-xs uppercase tracking-[0.18em] text-[#3A3A3A]"
            >
              <I size={16} strokeWidth={1.6} className="text-[#E8A6B8]" />
              <span className="font-medium">{p.label}</span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ---------- PROGRAMS GRID ---------- */
function ProgramsGrid({ programs, buy, buying, loading }) {
  return (
    <section id="programs" className="py-20 lg:py-28 scroll-mt-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="eyebrow mb-3">The Programs</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl text-[#3A3A3A]">
            Find your <span className="italic text-[#E8A6B8]">starting point</span>
          </h2>
          <p className="mt-5 text-[#5C5C5C]">
            Each program is structured, beginner-safe and built to help you
            train with intention — not guesswork.
          </p>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-3 gap-8">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="bg-white rounded-[2rem] border border-[#F3C9D3]/40 overflow-hidden animate-pulse"
              >
                <div className="aspect-[16/12] bg-[#F3C9D3]/30" />
                <div className="p-7 space-y-4">
                  <div className="h-3 w-24 bg-[#F3C9D3]/40 rounded" />
                  <div className="h-6 w-3/4 bg-[#F3C9D3]/40 rounded" />
                  <div className="h-3 w-full bg-[#F3C9D3]/30 rounded" />
                  <div className="h-3 w-5/6 bg-[#F3C9D3]/30 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-8">
            {programs.map((p, i) => (
              <ProgramCard
                key={p.id}
                program={p}
                buy={buy}
                buying={buying}
                index={i}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function ProgramCard({ program, buy, buying, index }) {
  const meta = PROGRAM_META[program.id] || {};
  return (
    <article
      id={program.id}
      className="group relative bg-white rounded-[2rem] overflow-hidden border border-[#F3C9D3]/40 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_24px_60px_rgba(232,166,184,0.25)] flex flex-col"
      data-testid={`program-card-${program.id}`}
      style={{ animationDelay: `${index * 100}ms` }}
    >
      <div className="relative aspect-[16/12] overflow-hidden bg-[#F3C9D3]/20">
        <img
          src={program.image_url}
          alt={`${program.title} — women's strength training program`}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        {meta.badge && (
          <span className="absolute top-5 left-5 bg-white/95 backdrop-blur-sm rounded-full px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] font-semibold text-[#3A3A3A] border border-white/60">
            {meta.badge}
          </span>
        )}
        <span
          className="absolute top-5 right-5 bg-[#3A3A3A] text-white backdrop-blur-sm rounded-full px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] font-semibold border border-[#3A3A3A]/40"
          data-testid={`program-coming-soon-${program.id}`}
        >
          Coming Soon
        </span>
      </div>
      <div className="p-8 flex-1 flex flex-col">
        <p className="text-[11px] uppercase tracking-[0.18em] text-[#E8A6B8] font-semibold">
          {program.tagline}
        </p>
        <h3 className="font-serif-display text-3xl text-[#3A3A3A] leading-tight mt-2">
          {program.title}
        </h3>
        <p className="mt-4 text-sm text-[#5C5C5C] leading-relaxed">
          {program.description}
        </p>

        {/* Meta row */}
        <dl className="mt-6 grid grid-cols-2 gap-3 text-left">
          <MetaItem icon={Calendar} label="Duration" value={meta.duration || "Self-paced"} />
          <MetaItem icon={TrendingUp} label="Level" value={meta.level || "Beginner"} />
          <MetaItem icon={Target} label="Focus" value={meta.focus || program.tagline} />
          <MetaItem icon={Sparkles} label="Goals" value={meta.goals || "Strength"} />
        </dl>

        {/* What's included */}
        {program.features?.length > 0 && (
          <>
            <p className="eyebrow mt-7 mb-3">What's Included</p>
            <ul className="space-y-2.5">
              {program.features.map((f) => (
                <li
                  key={f}
                  className="flex items-start gap-2.5 text-sm text-[#3A3A3A] leading-snug"
                >
                  <span className="mt-0.5 flex-shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#F3C9D3]/60">
                    <Check size={11} className="text-[#3A3A3A]" strokeWidth={2.5} />
                  </span>
                  {f}
                </li>
              ))}
            </ul>
          </>
        )}

        {/* Who this is for */}
        {meta.whoFor && (
          <>
            <p className="eyebrow mt-7 mb-3">Who It's For</p>
            <ul className="space-y-1.5">
              {meta.whoFor.map((w) => (
                <li key={w} className="text-xs text-[#5C5C5C] italic leading-snug">
                  · {w}
                </li>
              ))}
            </ul>
          </>
        )}

        {/* Price + CTAs */}
        <div className="mt-8 pt-6 border-t border-[#F3C9D3]/30 mt-auto">
          <div className="flex items-end justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#5C5C5C]">
                Launching soon
              </p>
              <p
                className="font-serif-display text-3xl text-[#3A3A3A] leading-none mt-1"
                data-testid={`program-price-${program.id}`}
              >
                {formatPrice(program)}
                <span className="text-base text-[#5C5C5C] ml-2 font-body italic">at launch</span>
              </p>
            </div>
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#E8A6B8] font-semibold text-right leading-tight">
              Currently being<br/>refined for launch
            </span>
          </div>
          <div className="mt-5 grid grid-cols-2 gap-3">
            <Link
              to={`/programs/${program.id}`}
              className="btn-outline text-xs"
              data-testid={`program-learn-${program.id}`}
            >
              Learn More
            </Link>
            <Link
              to="/booking"
              className="btn-pink text-xs"
              data-testid={`program-waitlist-${program.id}`}
              aria-label={`Join the waitlist for ${program.title}`}
            >
              <Sparkles size={14} /> Join Waitlist
            </Link>
          </div>
        </div>
      </div>
    </article>
  );
}

function MetaItem({ icon: Icon, label, value }) {
  return (
    <div className="bg-[#F6EFE9] rounded-xl px-3 py-2.5 border border-[#F3C9D3]/30">
      <dt className="text-[9px] uppercase tracking-[0.18em] text-[#5C5C5C] flex items-center gap-1.5">
        <Icon size={11} className="text-[#E8A6B8]" strokeWidth={1.8} />
        {label}
      </dt>
      <dd className="text-xs font-medium text-[#3A3A3A] mt-1 leading-snug">{value}</dd>
    </div>
  );
}

/* ---------- PROGRAM BENEFITS ---------- */
function ProgramBenefits() {
  return (
    <section className="bg-white py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="eyebrow mb-3">Why These Programs Work</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl text-[#3A3A3A]">
            Built for women, <span className="italic text-[#E8A6B8]">built to last</span>
          </h2>
          <p className="mt-5 text-[#5C5C5C]">
            These aren't men's templates with pink trim. They're real strength
            training for women — programmed by a women's strength coach who
            actually trains women every day.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
          {PROGRAM_BENEFITS.map((b, i) => {
            const I = b.icon;
            return (
              <article
                key={b.title}
                className="bg-[#F6EFE9] rounded-[1.75rem] p-7 border border-[#F3C9D3]/30 transition-all duration-300 hover:-translate-y-1 hover:bg-white hover:shadow-[0_18px_40px_rgba(232,166,184,0.2)]"
                data-testid={`program-benefit-${i}`}
              >
                <div className="inline-flex p-3 rounded-2xl bg-white border border-[#F3C9D3]/40 mb-5">
                  <I size={22} strokeWidth={1.5} className="text-[#E8A6B8]" />
                </div>
                <h3 className="font-serif-display text-xl text-[#3A3A3A] leading-snug">
                  {b.title}
                </h3>
                <p className="mt-3 text-sm text-[#5C5C5C] leading-relaxed">{b.body}</p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------- COMPARISON TABLE ---------- */
function Comparison({ programs }) {
  if (!programs || programs.length < 3) return null;
  const titles = programs.slice(0, 3).map((p) => p.title);
  return (
    <section className="bg-[#F3C9D3]/30 py-20 lg:py-28">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="eyebrow mb-3">Compare The Programs</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl text-[#3A3A3A]">
            Which one is <span className="italic text-[#E8A6B8]">right for you?</span>
          </h2>
        </div>

        {/* Desktop table */}
        <div className="hidden md:block bg-white rounded-[2rem] border border-[#F3C9D3]/40 overflow-hidden shadow-[0_14px_30px_rgba(232,166,184,0.12)]">
          <div className="grid grid-cols-4 bg-[#F6EFE9] border-b border-[#F3C9D3]/40">
            <div className="px-6 py-5 text-[11px] uppercase tracking-[0.18em] text-[#5C5C5C] font-semibold">
              Detail
            </div>
            {titles.map((t) => (
              <div
                key={t}
                className="px-6 py-5 font-serif-display text-xl text-[#3A3A3A] text-center"
              >
                {t}
              </div>
            ))}
          </div>
          {COMPARISON_ROWS.map((row, i) => (
            <div
              key={row.label}
              className={`grid grid-cols-4 ${
                i < COMPARISON_ROWS.length - 1 ? "border-b border-[#F3C9D3]/30" : ""
              }`}
              data-testid={`compare-row-${i}`}
            >
              <div className="px-6 py-5 text-[11px] uppercase tracking-[0.18em] font-semibold text-[#3A3A3A]">
                {row.label}
              </div>
              {row.values.map((v, j) => (
                <div
                  key={j}
                  className="px-6 py-5 text-sm text-[#5C5C5C] text-center leading-snug"
                >
                  {v}
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Mobile cards */}
        <div className="md:hidden grid gap-5">
          {titles.map((t, idx) => (
            <div
              key={t}
              className="bg-white rounded-[1.75rem] border border-[#F3C9D3]/40 p-6"
            >
              <p className="eyebrow mb-2">{t}</p>
              <dl className="space-y-2 mt-3">
                {COMPARISON_ROWS.map((row) => (
                  <div key={row.label} className="flex justify-between text-sm gap-4">
                    <dt className="text-[#5C5C5C]">{row.label}</dt>
                    <dd className="font-medium text-[#3A3A3A] text-right">
                      {row.values[idx]}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- TRANSFORMATIONS ---------- */
function Transformations() {
  return (
    <section className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="eyebrow mb-3">Real Women, Real Results</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl text-[#3A3A3A]">
            Transformations that go <span className="italic text-[#E8A6B8]">deeper than aesthetics</span>
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {TRANSFORMATION_TESTIMONIALS.map((t, i) => (
            <article
              key={t.name}
              className="bg-[#F6EFE9] rounded-[2rem] p-7 border border-[#F3C9D3]/30 transition-all duration-300 hover:-translate-y-1 hover:bg-white hover:shadow-[0_18px_40px_rgba(232,166,184,0.2)]"
              data-testid={`program-testimonial-${i}`}
            >
              <Quote size={22} className="text-[#E8A6B8]" strokeWidth={1.5} />
              <p className="font-serif-display italic text-xl text-[#3A3A3A] leading-snug mt-4">
                "{t.quote}"
              </p>
              <p className="mt-4 text-sm text-[#5C5C5C] leading-relaxed">{t.body}</p>
              <p className="mt-6 text-[11px] uppercase tracking-[0.18em] font-semibold text-[#3A3A3A]">
                {t.name}
                <span className="text-[#E8A6B8] mx-2">•</span>
                <span className="text-[#5C5C5C]">{t.role}</span>
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- FAQ ---------- */
function FAQ() {
  return (
    <section className="py-20 lg:py-28 bg-[#F6EFE9]">
      <div className="max-w-3xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-12">
          <p className="eyebrow mb-3">FAQ</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl text-[#3A3A3A]">
            Program <span className="italic text-[#E8A6B8]">questions</span>
          </h2>
        </div>
        <Accordion
          type="single"
          collapsible
          className="bg-white rounded-[2rem] border border-[#F3C9D3]/40 px-6 sm:px-8 divide-y divide-[#F3C9D3]/30"
        >
          {FAQ_ITEMS.map((item, i) => (
            <AccordionItem
              key={item.q}
              value={`item-${i}`}
              className="border-0"
              data-testid={`programs-faq-${i}`}
            >
              <AccordionTrigger className="text-left text-[#3A3A3A] font-medium text-base sm:text-lg py-6 hover:no-underline">
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-[#5C5C5C] text-sm sm:text-base leading-relaxed pb-6">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

/* ---------- FINAL CTA ---------- */
function FinalCTA() {
  return (
    <section className="px-6 lg:px-10 pb-24 pt-4">
      <div
        className="max-w-6xl mx-auto rounded-[2rem] overflow-hidden relative"
        style={{
          background:
            "linear-gradient(135deg, #E8A6B8 0%, #F3C9D3 50%, #F6EFE9 100%)",
        }}
      >
        <div className="relative px-6 sm:px-12 py-20 sm:py-24 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="eyebrow text-[#3A3A3A]/80 mb-4">Not Sure Which Program Fits?</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#3A3A3A] leading-tight">
              Let's find your <span className="italic">starting point</span> together
            </h2>
            <p className="mt-7 text-base sm:text-lg text-[#3A3A3A]/80 leading-relaxed max-w-xl">
              Book a free discovery call and Maya will help you choose the
              program — or coaching path — that genuinely fits your goals,
              experience and lifestyle.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link
                to="/booking"
                className="inline-flex items-center justify-center gap-2 bg-[#3A3A3A] hover:bg-black text-white rounded-full px-9 py-4 text-[12px] uppercase tracking-[0.18em] font-semibold transition-all hover:-translate-y-0.5 shadow-xl"
                data-testid="programs-final-cta"
              >
                Book Discovery Call <ArrowRight size={16} />
              </Link>
              <Link
                to="/services"
                className="btn-outline"
                data-testid="programs-services-link"
              >
                Explore Coaching
              </Link>
            </div>
          </div>
          <div className="hidden lg:flex flex-col gap-4 items-end">
            <div className="bg-white/80 backdrop-blur-md rounded-2xl px-6 py-5 max-w-sm">
              <Award size={20} className="text-[#E8A6B8]" strokeWidth={1.5} />
              <p className="mt-2 font-serif-display italic text-lg text-[#3A3A3A] leading-snug">
                "Strength is not about becoming smaller — it's about becoming more."
              </p>
            </div>
            <div className="bg-white/80 backdrop-blur-md rounded-2xl px-6 py-4 max-w-sm flex items-center gap-3">
              <PlayCircle size={22} className="text-[#E8A6B8] flex-shrink-0" strokeWidth={1.5} />
              <p className="text-sm text-[#3A3A3A] leading-snug">
                Every program includes technique guidance for every key lift.
              </p>
            </div>
            <div className="bg-white/80 backdrop-blur-md rounded-2xl px-6 py-4 max-w-sm flex items-center gap-3">
              <Users size={22} className="text-[#E8A6B8] flex-shrink-0" strokeWidth={1.5} />
              <p className="text-sm text-[#3A3A3A] leading-snug">
                500+ women have trained with HerFitHub since launch.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
